import { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { pricingPageAccess } from './platformAccess';
import {
  applyPricingWorkbook,
  createPricingFeature,
  downloadPricingWorkbook,
  getCommercialBeta,
  getPricingCatalog,
  initializePricingCatalog,
  listDealerAssignments,
  listDealers,
  previewPricingWorkbook,
  setCommercialBeta,
  updatePricingFeature,
} from '../../api/backendApi';
import { rowsFrom } from './dealerAccess';
import PricingUsagePanel from './PricingUsagePanel';

const FIELD_LABELS = {
  name: 'Feature',
  description: 'Description',
  wholesaleCad: 'Wholesale CAD',
  wholesaleUsd: 'Wholesale USD',
  allowance: 'Included Usage',
  overageCad: 'Overage CAD',
  overageUsd: 'Overage USD',
  active: 'Available to Dealers',
};

const METHOD_LABELS = {
  monthly_fixed: 'Monthly fee',
  per_unit_monthly: 'Count each month',
  consumption: 'Usage',
  included_overage: 'Included allowance, then overage',
};

const UNIT_LABELS = {
  tenant: 'Tenant',
  user: 'User',
  agent: 'Agent',
  device: 'Device',
  message: 'SMS message',
  minute: 'Voice minute',
};

const EMPTY = {
  featureId: '',
  name: '',
  description: '',
  billingMethod: 'monthly_fixed',
  billingUnit: 'tenant',
  wholesaleCad: '',
  wholesaleUsd: '',
  allowance: '',
  overageCad: '',
  overageUsd: '',
  active: false,
  catalogVersion: null,
};

function unwrap(response) {
  if (response && response.success === true && response.data) return response.data;
  return response || {};
}

function messageFrom(error) {
  const text = String(error?.message || '');
  const start = text.indexOf('{');
  if (start >= 0) {
    try {
      const body = JSON.parse(text.slice(start));
      return { message: body.message || text, code: body.error || '' };
    } catch {
      /* The response was not JSON. */
    }
  }
  return { message: text || 'The catalog could not be saved.', code: error?.code || '' };
}

function priceMissing(value) {
  return value == null || String(value).trim() === '';
}

function methodLabel(method) {
  return METHOD_LABELS[method] || method || 'Billing method';
}

function unitLabel(unit) {
  return UNIT_LABELS[unit] || unit || 'Unit';
}

function formatWhen(value) {
  const text = String(value || '').trim();
  if (!text) return 'Date unavailable';
  return text.replace('T', ' ').replace('Z', ' UTC');
}

function dealerDisplayName(dealer) {
  const name = String(dealer?.name || '').trim();
  const contact = String(dealer?.contactName || '').trim();
  if (!name) return 'Dealer name unavailable';
  return contact ? `${name} (${contact})` : name;
}

function tenantDisplayName(row) {
  const name = String(row?.tenantName || '').trim();
  return name || 'Tenant name unavailable';
}

export default function PricingPage() {
  const [tab, setTab] = useState('wholesale');
  const auth = useAuth() || {};
  const sections = pricingPageAccess({
    role: auth.role || auth.userData?.role,
    permissions: auth.userData?.permissions || auth.permissions,
  });
  const [features, setFeatures] = useState([]);
  const [metering, setMetering] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(EMPTY);
  const [creating, setCreating] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [initialization, setInitialization] = useState({ needed: false, missing: [] });
  const [confirmInit, setConfirmInit] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [beta, setBeta] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [tenantNames, setTenantNames] = useState({});
  const [betaDealerId, setBetaDealerId] = useState('');
  const [betaTenantId, setBetaTenantId] = useState('');
  const [dealerQuery, setDealerQuery] = useState('');
  const [tenantQuery, setTenantQuery] = useState('');
  const [betaSaving, setBetaSaving] = useState(false);

  const load = useCallback(async () => {
    const data = unwrap(await getPricingCatalog());
    setFeatures(data.features || []);
    setMetering(data.metering || []);
    setInitialization(data.initialization || { needed: false, missing: [] });
  }, []);

  useEffect(() => {
    let cancelled = false;
    getPricingCatalog()
      .then((response) => {
        if (cancelled) return;
        const data = unwrap(response);
        setFeatures(data.features || []);
        setMetering(data.metering || []);
        setInitialization(data.initialization || { needed: false, missing: [] });
      })
      .catch((err) => {
        if (!cancelled) setError(messageFrom(err).message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshBeta = useCallback(async () => {
    try {
      const data = unwrap(await getCommercialBeta());
      setBeta(data.canDesignate ? data : null);
    } catch {
      setBeta(null);
    }
  }, []);

  useEffect(() => {
    refreshBeta();
  }, [refreshBeta]);

  useEffect(() => {
    if (!beta) return undefined;
    let cancelled = false;
    listDealers()
      .then((response) => {
        if (!cancelled) setDealers(rowsFrom(response));
      })
      .catch(() => {
        if (!cancelled) setDealers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [beta]);

  useEffect(() => {
    if (!beta) return undefined;
    const dealerIds = [...new Set((beta.tenants || []).map((row) => row.dealerId).filter(Boolean))];
    if (!dealerIds.length) {
      setTenantNames({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(dealerIds.map((dealerId) => listDealerAssignments(dealerId).then(rowsFrom).catch(() => [])))
      .then((groups) => {
        if (cancelled) return;
        const names = {};
        groups.flat().forEach((row) => {
          if (row.tenantId) names[row.tenantId] = tenantDisplayName(row);
        });
        setTenantNames(names);
      });
    return () => {
      cancelled = true;
    };
  }, [beta]);

  useEffect(() => {
    if (!beta || !betaDealerId) {
      setAssignments([]);
      return undefined;
    }
    let cancelled = false;
    listDealerAssignments(betaDealerId)
      .then((response) => {
        if (cancelled) return;
        setAssignments(rowsFrom(response).filter((row) => String(row.status || '').toLowerCase() === 'active'));
      })
      .catch(() => {
        if (!cancelled) setAssignments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [beta, betaDealerId]);

  const eligibleDealers = useMemo(
    () => dealers.filter((dealer) => String(dealer.status || '').toLowerCase() === 'active'),
    [dealers]
  );
  const dealerById = useMemo(() => {
    const map = {};
    dealers.forEach((dealer) => {
      map[dealer.dealerId] = dealer;
    });
    return map;
  }, [dealers]);
  const betaDealerIds = useMemo(() => new Set((beta?.dealers || []).map((row) => row.dealerId)), [beta]);
  const betaTenantIds = useMemo(() => new Set((beta?.tenants || []).map((row) => row.tenantId)), [beta]);

  const selectedDealer = dealerById[betaDealerId];
  const selectedTenant = assignments.find((row) => row.tenantId === betaTenantId);

  async function saveBeta(scope, enabled) {
    if (!betaDealerId || (scope === 'tenant' && !betaTenantId)) {
      setError(scope === 'tenant' ? 'Choose the dealer, then one assigned tenant.' : 'Choose a dealer.');
      return;
    }
    const dealerName = selectedDealer ? dealerDisplayName(selectedDealer) : 'the selected dealer';
    const tenantName = selectedTenant ? tenantDisplayName(selectedTenant) : 'the selected tenant';
    const action = enabled ? 'Enable' : 'Disable';
    const target = scope === 'tenant' ? `${tenantName} for ${dealerName}` : dealerName;
    if (!window.confirm(`${action} commercial beta for ${target}? This does not create an invoice or change service access.`)) return;
    setBetaSaving(true);
    setError('');
    try {
      await setCommercialBeta({
        scope,
        dealerId: betaDealerId,
        ...(scope === 'tenant' ? { tenantId: betaTenantId } : {}),
        enabled,
      });
      setNotice(enabled ? 'Commercial beta is on for that selection. Amounts stay estimates.' : 'Commercial beta is off. Earlier beta usage stays an estimate.');
      await refreshBeta();
    } catch (err) {
      setError(messageFrom(err).message);
    } finally {
      setBetaSaving(false);
    }
  }

  function chooseDealer(dealerId) {
    setBetaDealerId(dealerId);
    setBetaTenantId('');
    setTenantQuery('');
  }

  const methods = useMemo(() => [...new Set(metering.map((row) => row.billingMethod))], [metering]);
  const units = useMemo(
    () => metering.filter((row) => row.billingMethod === draft.billingMethod).map((row) => row.billingUnit),
    [metering, draft.billingMethod]
  );
  const meter = metering.find((row) => row.billingMethod === draft.billingMethod && row.billingUnit === draft.billingUnit);

  function selectFeature(feature) {
    setCreating(false);
    setSelectedId(feature.featureId);
    setDraft({
      featureId: feature.featureId,
      name: feature.name || '',
      description: feature.description || '',
      billingMethod: feature.billingMethod,
      billingUnit: feature.billingUnit,
      wholesaleCad: feature.wholesaleCad || '',
      wholesaleUsd: feature.wholesaleUsd || '',
      allowance: feature.allowance || '',
      overageCad: feature.overageCad || '',
      overageUsd: feature.overageUsd || '',
      active: feature.active === true,
      catalogVersion: feature.catalogVersion,
    });
    setError('');
    setNotice('');
    setConflict(false);
    setTab('catalog');
  }

  function startNew() {
    setCreating(true);
    setSelectedId('');
    setDraft(EMPTY);
    setError('');
    setNotice('');
    setConflict(false);
    setTab('catalog');
  }

  function updateField(field, value) {
    setDraft((current) => {
      const next = { ...current, [field]: value };
      if (field === 'billingMethod') {
        const allowed = metering.filter((row) => row.billingMethod === value).map((row) => row.billingUnit);
        if (!allowed.includes(next.billingUnit)) next.billingUnit = allowed[0] || '';
        next.allowance = '';
        next.overageCad = '';
        next.overageUsd = '';
      }
      return next;
    });
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    setConflict(false);
    const payload = {
      name: draft.name,
      description: draft.description,
      billingMethod: draft.billingMethod,
      billingUnit: draft.billingUnit,
      wholesaleCad: draft.wholesaleCad,
      wholesaleUsd: draft.wholesaleUsd,
      active: draft.active === true,
    };
    if (draft.allowance) payload.allowance = draft.allowance;
    if (draft.overageCad) payload.overageCad = draft.overageCad;
    if (draft.overageUsd) payload.overageUsd = draft.overageUsd;
    try {
      if (creating) {
        await createPricingFeature({ ...payload, featureId: draft.featureId.trim() });
      } else {
        await updatePricingFeature(selectedId, { ...payload, catalogVersion: draft.catalogVersion });
      }
      await load();
      setNotice(creating ? 'Feature added. It stays inactive until you make it available to dealers.' : 'Wholesale pricing saved.');
      if (creating) startNew();
    } catch (err) {
      const parsed = messageFrom(err);
      setError(parsed.message);
      setConflict(parsed.code === 'catalog_changed' || parsed.code === 'version_required');
    } finally {
      setSaving(false);
    }
  }

  async function reloadSaved() {
    setError('');
    setConflict(false);
    try {
      const data = unwrap(await getPricingCatalog());
      const rows = data.features || [];
      setFeatures(rows);
      setMetering(data.metering || []);
      setInitialization(data.initialization || { needed: false, missing: [] });
      const current = rows.find((feature) => feature.featureId === selectedId);
      if (current) selectFeature(current);
    } catch (err) {
      setError(messageFrom(err).message);
    }
  }

  async function initializeCatalog() {
    if (initializing) return;
    setInitializing(true);
    setError('');
    try {
      await initializePricingCatalog();
      setConfirmInit(false);
      setNotice('Supported features were added as inactive entries. Wholesale prices are still blank.');
      await load();
    } catch (err) {
      setError(messageFrom(err).message);
    } finally {
      setInitializing(false);
    }
  }

  async function exportWorkbook() {
    setExporting(true);
    setError('');
    try {
      await downloadPricingWorkbook();
    } catch (err) {
      setError(messageFrom(err).message);
    } finally {
      setExporting(false);
    }
  }

  async function importWorkbook(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || previewing) return;
    setPreviewing(true);
    setError('');
    setPreview(null);
    try {
      const response = await previewPricingWorkbook(file);
      setPreview(response?.data || null);
    } catch (err) {
      setError(messageFrom(err).message);
    } finally {
      setPreviewing(false);
    }
  }

  async function applyWorkbook() {
    if (!preview?.previewId || applying || previewing) return;
    setApplying(true);
    setError('');
    try {
      await applyPricingWorkbook(preview.previewId);
      setPreview(null);
      setNotice('Wholesale prices from the workbook were saved.');
      await load();
    } catch (err) {
      const parsed = messageFrom(err);
      setError(parsed.message);
      if (parsed.code === 'catalog_changed' || parsed.code === 'preview_expired') {
        setPreview((current) => (current ? { ...current, blocked: true } : current));
      }
    } finally {
      setApplying(false);
    }
  }

  const showPolicies = draft.billingMethod === 'included_overage' || draft.billingMethod === 'per_unit_monthly';
  const previewChanges = preview?.changes || [];
  const canApply = Boolean(preview?.previewId) && previewChanges.length > 0 && !preview?.blocked && !applying;
  const activeCount = features.filter((feature) => feature.active === true).length;
  const awaitingCount = features.filter((feature) => priceMissing(feature.wholesaleCad) || priceMissing(feature.wholesaleUsd)).length;
  const versions = features.map((feature) => Number(feature.catalogVersion)).filter((version) => Number.isFinite(version) && version > 0);
  const catalogVersion = versions.length === 0
    ? 'Not available'
    : (versions.every((version) => version === versions[0]) ? String(versions[0]) : `${Math.min(...versions)}–${Math.max(...versions)}`);
  const tabs = [
    ['wholesale', 'Wholesale Pricing'],
    ['catalog', 'Feature Catalog'],
    ['usage', 'Usage & Revenue'],
    ['policies', 'Usage Policies'],
    ...(beta && sections.beta ? [['beta', 'Commercial Beta']] : []),
  ].filter(([id]) => sections[id] !== false);
  const visibleKey = tabs.map(([id]) => id).join(',');
  useEffect(() => {
    const ids = visibleKey.split(',').filter(Boolean);
    if (ids.length && !ids.includes(tab)) setTab(ids[0]);
  }, [tab, visibleKey]);

  return (
    <MainLayout scrollMain>
      <div className="w-full px-4 py-6 sm:px-6" data-testid="pricing-page">
        <h1 className="text-2xl font-semibold text-gray-900">Pricing</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          Set Signal Bridge wholesale prices, then review usage. Prices are stored as entered in CAD and USD. Saving a price does not change tenant access, dealer retail prices, or Twilio vendor costs.
        </p>

        <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Pricing sections">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`rounded border px-3 py-1.5 text-sm ${tab === id ? 'border-gray-900 bg-white font-medium text-gray-900' : 'border-gray-200 bg-gray-50 text-gray-600'}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            <p>{error}</p>
            {conflict ? (
              <button type="button" className="mt-2 rounded border border-red-300 bg-white px-2 py-1 text-red-800" onClick={reloadSaved}>
                Reload saved version
              </button>
            ) : null}
          </div>
        ) : null}
        {notice ? <p className="mt-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p> : null}

        {tab === 'wholesale' ? (
          <section className="mt-4">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <SummaryCard label="Catalog features" value={String(features.length)} />
              <SummaryCard label="Available to dealers" value={String(activeCount)} />
              <SummaryCard label="Awaiting a price" value={String(awaitingCount)} />
              <SummaryCard label="Catalog version" value={catalogVersion} />
            </div>
            <ol className="mt-4 list-decimal space-y-1 pl-5 text-sm text-gray-700">
              {initialization.needed ? <li>Initialize the supported features. Prices stay blank.</li> : null}
              <li>Export the price list.</li>
              <li>Enter wholesale prices in the workbook. A blank cell keeps the saved price, and 0 means zero.</li>
              <li>Import the workbook and review the preview.</li>
              <li>Apply the preview. Nothing is saved before that.</li>
            </ol>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {initialization.needed ? (
                <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white" onClick={() => setConfirmInit(true)} disabled={initializing}>
                  Initialize Catalog
                </button>
              ) : null}
              <button type="button" className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm" onClick={exportWorkbook} disabled={exporting}>
                {exporting ? 'Exporting…' : 'Export Price List'}
              </button>
              <label className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm">
                {previewing ? 'Reading workbook…' : 'Import Price List'}
                <input aria-label="Import Price List" className="sr-only" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={importWorkbook} disabled={previewing || applying} />
              </label>
            </div>
            <p className="mt-3 max-w-3xl text-sm text-gray-600">
              Leave a feature out of the workbook to skip it. The workbook cannot create or delete features, and existing subscriptions stay as they are.
            </p>
            {features.length === 0 ? (
              <p className="mt-4 rounded border border-gray-200 bg-white px-4 py-6 text-sm text-gray-600">
                The price list has no features because none have been saved yet. Export only includes saved catalog entries, so the workbook would contain headers and no feature rows. Initialize the supported features, then export and enter wholesale prices. New features stay inactive until you make them available to dealers.
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto rounded border border-gray-200 bg-white">
                <table className="w-full min-w-[36rem] text-left text-sm">
                  <thead className="text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Feature</th>
                      <th className="px-4 py-2 font-medium">Billing</th>
                      <th className="px-4 py-2 font-medium">CAD</th>
                      <th className="px-4 py-2 font-medium">USD</th>
                      <th className="px-4 py-2 font-medium">Dealers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature) => (
                      <tr key={feature.featureId}>
                        <td className="px-4 py-2">{feature.name}</td>
                        <td className="px-4 py-2 text-gray-700">{metering.find((row) => row.billingMethod === feature.billingMethod && row.billingUnit === feature.billingUnit)?.billingBasis || methodLabel(feature.billingMethod)}</td>
                        <td className="px-4 py-2">{priceMissing(feature.wholesaleCad) ? '—' : feature.wholesaleCad}</td>
                        <td className="px-4 py-2">{priceMissing(feature.wholesaleUsd) ? '—' : feature.wholesaleUsd}</td>
                        <td className="px-4 py-2">{feature.active ? 'Available' : 'Not available'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ) : null}

        {tab === 'catalog' ? (
          <div className="mt-4 grid gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]">
            <section className="overflow-x-auto rounded border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <h2 className="text-base font-medium text-gray-900">Features</h2>
                <button type="button" className="text-sm text-blue-700" onClick={startNew}>Add feature</button>
              </div>
              {features.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-500">No features yet. Initialize them from Wholesale Pricing, or add one here.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Feature</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature) => (
                      <tr key={feature.featureId} className={selectedId === feature.featureId ? 'bg-gray-50' : ''}>
                        <td className="px-4 py-2">
                          <button type="button" className="text-left text-blue-700" onClick={() => selectFeature(feature)}>
                            {feature.name}
                          </button>
                        </td>
                        <td className="px-4 py-2">{feature.active ? 'Available' : 'Not available'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <form className="rounded border border-gray-200 bg-white p-4" onSubmit={save}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-medium text-gray-900">{creating ? 'New feature' : draft.name || 'Edit feature'}</h2>
                  <p className="mt-1 text-xs text-gray-500">
                    {creating
                      ? 'Enter the prices, then save. A new feature stays unavailable to dealers unless you turn that on.'
                      : 'Enter the wholesale prices, then save. The feature code cannot be changed.'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="inline-flex items-center text-sm text-gray-700">
                    <input type="checkbox" className="mr-2" checked={draft.active === true} onChange={(event) => updateField('active', event.target.checked)} />
                    Available to dealers
                  </label>
                  <button type="submit" disabled={saving} className="rounded bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-60">
                    {saving ? 'Saving…' : 'Save wholesale feature'}
                  </button>
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <label className="block text-sm text-gray-700">
                  Wholesale CAD
                  <input aria-label="Wholesale CAD" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" value={draft.wholesaleCad} onChange={(event) => updateField('wholesaleCad', event.target.value)} />
                </label>
                <label className="block text-sm text-gray-700">
                  Wholesale USD
                  <input aria-label="Wholesale USD" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" value={draft.wholesaleUsd} onChange={(event) => updateField('wholesaleUsd', event.target.value)} />
                </label>
                <label className="block text-sm text-gray-700">
                  How it is billed
                  <select aria-label="Billing method" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" value={draft.billingMethod} onChange={(event) => updateField('billingMethod', event.target.value)}>
                    {(methods.length ? methods : [draft.billingMethod]).map((method) => <option key={method} value={method}>{methodLabel(method)}</option>)}
                  </select>
                </label>
                <label className="block text-sm text-gray-700">
                  What is counted
                  <select aria-label="Billing unit" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" value={draft.billingUnit} onChange={(event) => updateField('billingUnit', event.target.value)}>
                    {(units.length ? units : [draft.billingUnit]).map((unit) => <option key={unit} value={unit}>{unitLabel(unit)}</option>)}
                  </select>
                </label>
                <label className="block text-sm text-gray-700">
                  Name
                  <input aria-label="Feature name" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" value={draft.name} onChange={(event) => updateField('name', event.target.value)} />
                </label>
                <label className="block text-sm text-gray-700">
                  Feature code
                  <input
                    aria-label="Feature ID"
                    className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 disabled:bg-gray-100"
                    value={draft.featureId}
                    disabled={!creating}
                    onChange={(event) => updateField('featureId', event.target.value)}
                  />
                </label>
                <label className="block text-sm text-gray-700 md:col-span-2">
                  Description
                  <textarea aria-label="Description" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" rows={2} value={draft.description} onChange={(event) => updateField('description', event.target.value)} />
                </label>
                {showPolicies ? (
                  <label className="block text-sm text-gray-700">
                    Included amount
                    <input aria-label="Allowance" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" value={draft.allowance} onChange={(event) => updateField('allowance', event.target.value)} />
                  </label>
                ) : null}
                {draft.billingMethod === 'included_overage' ? (
                  <>
                    <label className="block text-sm text-gray-700">
                      Overage CAD
                      <input aria-label="Overage CAD" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" value={draft.overageCad} onChange={(event) => updateField('overageCad', event.target.value)} />
                    </label>
                    <label className="block text-sm text-gray-700">
                      Overage USD
                      <input aria-label="Overage USD" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5" value={draft.overageUsd} onChange={(event) => updateField('overageUsd', event.target.value)} />
                    </label>
                  </>
                ) : null}
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Enter a number such as 0.015. CAD and USD are stored separately and are not converted.
                {showPolicies ? ' Leave Included amount blank when this feature has no included amount.' : ''}
                {!creating && draft.catalogVersion ? ` Saved version ${draft.catalogVersion}.` : ''}
              </p>
              {meter ? <p className="mt-1 text-xs text-gray-500">{meter.billingBasis ? `${meter.billingBasis}. ` : ''}{meter.meter}</p> : null}
            </form>
          </div>
        ) : null}

        {tab === 'usage' ? <PricingUsagePanel /> : null}

        {tab === 'policies' ? (
          <section className="mt-4 overflow-x-auto rounded border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-medium text-gray-900">How usage is counted</h2>
              <p className="mt-1 max-w-3xl text-sm text-gray-600">These descriptions explain the catalog. They do not change a tenant subscription or create a charge.</p>
            </div>
            {metering.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500">Usage policies will appear when the catalog finishes loading.</p>
            ) : (
              <table className="w-full min-w-[36rem] text-left text-sm">
                <thead className="text-gray-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Billing</th>
                    <th className="px-4 py-2 font-medium">What is counted</th>
                    <th className="px-4 py-2 font-medium">How it is charged</th>
                  </tr>
                </thead>
                <tbody>
                  {metering.map((row) => (
                    <tr key={`${row.billingMethod}-${row.billingUnit}`}>
                      <td className="px-4 py-2">{methodLabel(row.billingMethod)}</td>
                      <td className="px-4 py-2">{unitLabel(row.billingUnit)}</td>
                      <td className="px-4 py-2 text-gray-700">{row.billingBasis || row.meter}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        ) : null}

        {tab === 'beta' && beta && sections.beta ? (
          <section className="mt-4 rounded border border-amber-200 bg-amber-50 p-4">
            <h2 className="text-base font-medium text-gray-900">Commercial beta</h2>
            <p className="mt-1 max-w-3xl text-sm text-gray-700">BETA ESTIMATE — NOT BILLABLE. Beta usage is a test of the commercial calculation. It does not create an invoice, collect payment, or turn features off.</p>
            <div className="mt-4 overflow-x-auto rounded border border-amber-100 bg-white">
              {(beta.dealers || []).length || (beta.tenants || []).length ? (
                <table className="w-full min-w-[36rem] text-left text-sm">
                  <thead className="text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Participant</th>
                      <th className="px-4 py-2 font-medium">Type</th>
                      <th className="px-4 py-2 font-medium">Dealer</th>
                      <th className="px-4 py-2 font-medium">Beta since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(beta.dealers || []).map((row) => (
                      <tr key={`dealer-${row.dealerId}`}>
                        <td className="px-4 py-2">{dealerById[row.dealerId] ? dealerDisplayName(dealerById[row.dealerId]) : 'Dealer name unavailable'}</td>
                        <td className="px-4 py-2">Dealer</td>
                        <td className="px-4 py-2">—</td>
                        <td className="px-4 py-2">{formatWhen(row.enabledAt)}</td>
                      </tr>
                    ))}
                    {(beta.tenants || []).map((row) => (
                      <tr key={`tenant-${row.dealerId}-${row.tenantId}`}>
                        <td className="px-4 py-2">{tenantNames[row.tenantId] || 'Tenant name unavailable'}</td>
                        <td className="px-4 py-2">Tenant</td>
                        <td className="px-4 py-2">{dealerById[row.dealerId] ? dealerDisplayName(dealerById[row.dealerId]) : 'Dealer name unavailable'}</td>
                        <td className="px-4 py-2">{formatWhen(row.enabledAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="px-4 py-4 text-sm text-gray-700">No dealer or tenant is in commercial beta.</p>}
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <NamePicker
                label="Dealer"
                query={dealerQuery}
                onQuery={setDealerQuery}
                options={eligibleDealers.map((dealer) => ({
                  id: dealer.dealerId,
                  name: dealerDisplayName(dealer),
                  status: betaDealerIds.has(dealer.dealerId) ? 'In beta' : 'Not in beta',
                }))}
                selectedId={betaDealerId}
                onSelect={chooseDealer}
                empty="No active dealers match that name."
              />
              <NamePicker
                label="Assigned tenant"
                query={tenantQuery}
                onQuery={setTenantQuery}
                options={assignments.map((row) => ({
                  id: row.tenantId,
                  name: tenantDisplayName(row),
                  status: betaTenantIds.has(row.tenantId) ? 'In beta' : 'Not in beta',
                }))}
                selectedId={betaTenantId}
                onSelect={setBetaTenantId}
                empty={betaDealerId ? 'No assigned tenant matches that name.' : 'Choose a dealer to see assigned tenants.'}
                disabled={!betaDealerId}
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white" disabled={betaSaving} onClick={() => saveBeta('dealer', true)}>Enable dealer beta</button>
              <button type="button" className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm" disabled={betaSaving} onClick={() => saveBeta('dealer', false)}>Disable dealer beta</button>
              <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white" disabled={betaSaving} onClick={() => saveBeta('tenant', true)}>Enable tenant beta</button>
              <button type="button" className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm" disabled={betaSaving} onClick={() => saveBeta('tenant', false)}>Disable tenant beta</button>
            </div>
          </section>
        ) : null}

        {preview ? (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Preview Changes">
            <div className="mt-10 max-h-[80vh] w-full max-w-3xl overflow-auto rounded bg-white p-4 shadow">
              <h2 className="text-lg font-medium text-gray-900">Preview Changes</h2>
              <p className="mt-1 text-sm text-gray-600">
                Records affected: {preview.counts?.changed || 0}. {preview.counts?.unchanged || 0} unchanged, {preview.counts?.omitted || 0} omitted.
                {preview.expiresAt ? ` This preview expires ${preview.expiresAt}.` : ''}
              </p>
              {(preview.warnings || []).length > 0 ? (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-amber-800">
                  {preview.warnings.map((item, index) => (
                    <li key={`warn-${item.field}-${index}`}>{item.message}</li>
                  ))}
                </ul>
              ) : null}
              {(preview.errors || []).length > 0 ? (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-red-700">
                  {preview.errors.map((item, index) => (
                    <li key={`${item.row}-${item.field}-${index}`}>
                      Row {item.row}{item.featureId ? ` (${item.featureId})` : ''}{item.field ? ` ${item.field}` : ''}: {item.message}
                    </li>
                  ))}
                </ul>
              ) : null}
              {previewChanges.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="mt-3 w-full min-w-[32rem] text-left text-sm">
                    <thead className="text-gray-500">
                      <tr>
                        <th className="py-1 pr-3">Feature</th>
                        <th className="py-1 pr-3">Field</th>
                        <th className="py-1 pr-3">Current</th>
                        <th className="py-1">New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewChanges.map((change) => (change.fields || []).map((field) => (
                        <tr key={`${change.featureId}-${field.field}`}>
                          <td className="py-1 pr-3">{change.name || change.featureId}</td>
                          <td className="py-1 pr-3">{FIELD_LABELS[field.field] || field.field}</td>
                          <td className="py-1 pr-3">{field.old === '' || field.old == null ? '—' : String(field.old)}</td>
                          <td className="py-1">{field.new === '' || field.new == null ? '—' : String(field.new)}</td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {!preview.previewId ? <p className="mt-3 text-sm text-gray-700">Fix the workbook and import it again. Nothing was saved.</p> : null}
              {preview.previewId && previewChanges.length === 0 ? <p className="mt-3 text-sm text-gray-700">The workbook matches the saved catalog. Nothing will change.</p> : null}
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <button type="button" className="rounded border border-gray-300 px-3 py-1.5 text-sm" onClick={() => setPreview(null)} disabled={applying}>
                  Cancel
                </button>
                {canApply ? (
                  <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white disabled:opacity-60" onClick={applyWorkbook} disabled={applying}>
                    {applying ? 'Applying…' : 'Apply Changes'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        {confirmInit ? (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Initialize Catalog">
            <div className="mt-16 w-full max-w-lg rounded bg-white p-4 shadow">
              <h2 className="text-lg font-medium text-gray-900">Initialize Catalog</h2>
              <p className="mt-2 text-sm text-gray-700">
                This adds the supported wholesale features that are not already in the catalog. They are inactive, and wholesale prices are left blank. Existing features, dealer subscriptions, and vendor costs are not changed. Nothing is published to dealers until you set prices and mark a feature available.
              </p>
              {initialization.missing?.length ? (
                <ul className="mt-3 list-disc pl-5 text-sm text-gray-700">
                  {initialization.missing.map((feature) => (
                    <li key={feature.featureId}>{feature.name}</li>
                  ))}
                </ul>
              ) : null}
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <button type="button" className="rounded border border-gray-300 px-3 py-1.5 text-sm" onClick={() => setConfirmInit(false)} disabled={initializing}>
                  Cancel
                </button>
                <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white disabled:opacity-60" onClick={initializeCatalog} disabled={initializing}>
                  {initializing ? 'Initializing…' : 'Initialize features'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </MainLayout>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded border border-gray-200 bg-white px-4 py-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-medium text-gray-900">{value}</p>
    </div>
  );
}

function NamePicker({ label, query, onQuery, options, selectedId, onSelect, empty, disabled = false }) {
  const selected = options.find((option) => option.id === selectedId);
  const visible = options.filter((option) => option.name.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <div>
      <label className="block text-sm text-gray-700">
        {label}
        <input
          aria-label={label}
          className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1.5 disabled:bg-gray-100"
          value={query}
          placeholder="Search by name"
          disabled={disabled}
          onChange={(event) => onQuery(event.target.value)}
        />
      </label>
      {selected ? <p className="mt-1 text-xs text-gray-600">Selected: {selected.name} · {selected.status}</p> : null}
      <ul className="mt-2 max-h-48 overflow-auto rounded border border-gray-200 bg-white" role="listbox" aria-label={`${label} choices`}>
        {visible.length === 0 ? <li className="px-3 py-2 text-sm text-gray-500">{empty}</li> : null}
        {visible.map((option) => (
          <li key={option.id}>
            <button
              type="button"
              role="option"
              aria-selected={option.id === selectedId}
              className={`block w-full px-3 py-2 text-left text-sm ${option.id === selectedId ? 'bg-gray-100 font-medium' : ''}`}
              onClick={() => onSelect(option.id)}
            >
              {option.name}
              <span className="mt-0.5 block text-xs text-gray-500">{option.status}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
